import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Camera, Loader2 } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GalleryPage = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const response = await fetch(`${API}/photos`);
        const data = await response.json();
        setPhotos(data);
      } catch (error) {
        console.error('Failed to fetch photos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPhotos();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="gallery-page">
      <Navbar />
      
      {/* Header */}
      <section className="bg-gradient-to-r from-sip-pink to-sip-pink/80 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Camera className="w-16 h-16 mx-auto mb-4 text-white" />
            <h1 className="font-script text-4xl md:text-6xl text-white mb-4">
              Gallery
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Snapshots of good times and great sips!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20" data-testid="loading-spinner">
              <Loader2 className="w-12 h-12 text-sip-pink animate-spin mx-auto" />
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-20" data-testid="no-photos">
              <div className="card-graffiti inline-block">
                <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-xl text-gray-600">
                  No photos yet.
                </p>
                <p className="text-gray-500 mt-2">
                  Check back soon!
                </p>
              </div>
            </div>
          ) : (
            <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
              {photos.map((photo, index) => (
                <motion.div
                  key={photo.photo_id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="break-inside-avoid cursor-pointer"
                  onClick={() => setSelectedPhoto(photo)}
                  data-testid={`gallery-photo-${index}`}
                >
                  <div className="sticker group relative overflow-hidden">
                    <img
                      src={photo.url}
                      alt={photo.caption || 'Gallery photo'}
                      className="w-full h-auto"
                    />
                    {photo.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-sm">{photo.caption}</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
          data-testid="lightbox"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative max-w-4xl max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedPhoto.url}
              alt={selectedPhoto.caption || 'Gallery photo'}
              className="max-w-full max-h-[80vh] object-contain rounded-2xl"
            />
            {selectedPhoto.caption && (
              <div className="mt-4 text-center">
                <p className="text-white text-lg">{selectedPhoto.caption}</p>
              </div>
            )}
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute -top-4 -right-4 bg-sip-pink text-white w-10 h-10 rounded-full flex items-center justify-center font-bold hover:bg-sip-pink/80 transition-colors"
              data-testid="close-lightbox"
            >
              ✕
            </button>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GalleryPage;
